import { Box, Button, Chip, TextField, Typography, Grid } from '@mui/material';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import RequiredLabel from '../UtilityComponent/RequiredLabel';
import { processPurchaseOrderNumbers, validateAndProcessInputValue } from '../UtilityComponent/validationUtils'; // Adjust the import path as needed
import './BillingInformation.css'; // Import CSS for styling
import { useSelector } from 'react-redux';
import { getBusinessUnit } from '../../store/APISlice';

export const nameFieldNames = ['purchaseOrderNumber', 'pickupNumber'];

interface BillingInformationProps {
  onNext: () => void;
  onBack: () => void;
  values: { purchaseOrderNumber: string; pickupNumber: string; additionalPurchaseOrder: string[]; purchaseOrderNumberList?: string[]; };
  errors: {
    errorMessage: any; purchaseOrderNumber?: string; pickupNumber?: string; additionalPurchaseOrder?: string[];
  };
  touched: { purchaseOrderNumber?: boolean; pickupNumber?: boolean; additionalPurchaseOrder?: boolean[]; };
  requestType: string;
}

const styles: { [key: string]: CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    maxWidth: '80%',
    margin: 'auto',
    marginTop: '10px',
  },
  title: {
    color: 'blue',
    fontSize: '1rem'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr', // 4 columns
    gap: '10px',
    width: '100%',
    marginTop: '20px',
    marginBottom: '10px',
  },
  fieldGroup: {
    display: 'contents', // Use 'contents' to make the grid layout work properly
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '0px', // Further reduced bottom margin
    paddingBottom: '0px', // Remove padding bottom
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column', // Stack label and input vertically
    alignItems: 'flex-start',
    gap: '5px',
  },
  chipsContainer: {
    display: 'flex',
    overflowX: 'scroll', // Always show horizontal scrollbar
    overflowY: 'hidden', // No vertical scroll
    whiteSpace: 'nowrap', // Prevent wrapping of chips
    padding: '10px 0', // Add some padding for aesthetics
    border: '1px solid #ccc', // Add border
    borderRadius: '4px', // Optional: Add border radius
    maxWidth: '300px', // Set a fixed width to prevent expansion
    height: '40px', // Set a fixed height for the chips container
    width: '100%', // Ensure it takes the full width of the input container
    scrollbarWidth: 'thin', // Firefox: Make scrollbar thinner
    scrollbarColor: '#888 #ccc', // Firefox: Set scrollbar color
  },
  chip: {
    marginRight: '10px', // Space between chips
  },
  customNote: {
    fontFamily: 'Helvetica Neue',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '14.65px',
    textAlign: 'left',
    textUnderlinePosition: 'from-font',
    textDecorationSkipInk: 'none',
    color: '#626262', // Set the text color
  },
  ulCls: {
    listStyle: 'none',
    display: 'flex',
    padding: '4px',
    margin: '0px',
    flexWrap: 'wrap'
  },
  liCls: {
    paddingTop: '5px',
    paddingBottom: '5px'
  },
  '@media (max-width: 768px)': {
    formRow: {
      gridTemplateColumns: '1fr', // 1 column on small screens
    },
    fieldContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
  } as CSSProperties
};

const BillingInformation: React.FC<BillingInformationProps> = ({ onNext, onBack, values, errors, touched, requestType }) => {
  const { handleChange, setFieldValue, setErrors, setFieldError, setFieldTouched } = useFormikContext<any>();
  const [inputValue, setInputValue] = useState<string>('');
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedBu = useSelector(getBusinessUnit);

  useEffect(() => {
    const purchaseOrderNumber = values.purchaseOrderNumber;
    console.log('purc',purchaseOrderNumber)
    if (purchaseOrderNumber) {
      processPurchaseOrderNumbers(purchaseOrderNumber, setFieldTouched, setFieldError, setFieldValue, setLocalErrorMessage);
    } else {
      setFieldValue('purchaseOrderNumberList', []);
      setFieldError('purchaseOrderNumber', ''); // Clear any existing error
    }
  }, [values.purchaseOrderNumber, setFieldValue, setFieldError, setFieldTouched, setLocalErrorMessage]);

  const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && e.currentTarget.selectionStart === 0) {
      setFieldValue('purchaseOrderNumber', '');
      setInputValue('');
    }
  };

  const handleDeleteChip = (index: number) => {
    const updatedList = values.purchaseOrderNumberList?.filter((_, i) => i !== index) || [];
    setFieldValue('purchaseOrderNumberList', updatedList);
    setFieldValue('purchaseOrderNumber', updatedList.join('; '));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setLocalErrorMessage(null); // Reset error message
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault(); // Prevent default behavior for Enter and Tab keys
      validateAndProcessInputValue(inputValue, values, setFieldValue, setLocalErrorMessage, requestType);
      console.log('local',localErrorMessage)
      setInputValue(''); // Clear the input value after processing
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      validateAndProcessInputValue(inputValue, values, setFieldValue, setLocalErrorMessage, requestType);
      console.log('local',localErrorMessage)
      setInputValue(''); // Clear the input value after processing
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputValue]);

  const handleBack = () => {
    setErrors({});
    onBack();
  };

  const handleNext = () => {
    validateAndProcessInputValue(inputValue, values, setFieldValue, setLocalErrorMessage, requestType);
    onNext();
  };

  return (
    <>
      <div>
        <h2 className="billing-header">Billing Information</h2>
        <Grid
  container
  spacing={3}
  justifyContent="center"
  sx={{
    padding: { xs: '0 1rem', sm: '0 2rem' }, // Add left and right padding for responsiveness
  }}
>
  {/* First Column: Empty */}
  <Grid item xs={12} sm={3}></Grid>

  {/* Second Column: Purchase Order Number */}
  <Grid
    item
    xs={12}
    sm={3}
    sx={{
      margin: { xs: '0 1rem', sm: '0' }, // Add left and right margin for smaller screens
    }}
  >
    <RequiredLabel label="Purchase Order Number" mandatory={true} />
    <TextField
      id="purchaseOrderNumber"
      name="purchaseOrderNumber"
      type="text"
      variant="outlined"
      size="small"
      margin="dense"
      InputProps={{
        style: {
          fontSize: '0.8rem',
        },
      }}
      ref={inputRef}
      autoComplete="off"
      placeholder="Please enter Purchase order"
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onKeyUp={handleBackspace}
      fullWidth
    />
    <div className="error-container">
      {touched.purchaseOrderNumber && errors.purchaseOrderNumber && (
        <div className="error-message">{errors.purchaseOrderNumber}</div>
      )}
    </div>

    <div className="chips-container">
      <ul style={styles.ulCls}>
        {values.purchaseOrderNumberList &&
          values.purchaseOrderNumberList.map((po, index) => (
            <li style={styles.liCls} key={index}>
              <Chip
                label={po}
                onDelete={() => handleDeleteChip(index)}
                style={{ marginRight: '3px' }}
              />
            </li>
          ))}
      </ul>
    </div>
    {selectedBu === 'PQGI' && (
      <small style={styles.customNote}>
        *Purchase Order number should be a 10-digit code starting with 45 or 76
      </small>
    )}
    {selectedBu === 'PQGI' && localErrorMessage && (
      <div className="error-message">{localErrorMessage}</div>
    )}
  </Grid>

  {/* Third Column: Pickup Number */}
  <Grid
    item
    xs={12}
    sm={3}
    sx={{
      margin: { xs: '0 1rem', sm: '0' }, // Add left and right margin for smaller screens
    }}
  >
    <RequiredLabel label="Pickup Number" mandatory={true} />
    <TextField
      id="pickupNumber"
      name="pickupNumber"
      type="text"
      variant="outlined"
      size="small"
      fullWidth
      margin="dense"
      InputProps={{
        style: {
          fontSize: '0.8rem',
        },
      }}
      autoComplete="off"
      value={values.pickupNumber}
      placeholder="Please enter Pickup number"
      onChange={(e) => setFieldValue('pickupNumber', e.target.value)}
    />
    <ErrorMessage name="pickupNumber" component="div" className="error-message" />
    {selectedBu !== 'PBCI' && (
      <p style={styles.customNote}>
        * If No Pickup Number Exists, Please Put in PO Number
      </p>
    )}
  </Grid>

  {/* Fourth Column: Empty */}
  <Grid item xs={12} sm={3}></Grid>
</Grid>
      </div>

      <Field name="requestType" type="hidden" value={requestType} />

        <Box
        sx={{
          position: 'fixed', // Keep the buttons fixed at the bottom
          bottom: '60px', // Distance from the bottom of the screen
          left: '8rem', // Maintain position on normal screens
          width: 'calc(100% - 16rem)', // Adjust width dynamically based on left margin
          display: 'flex',
          flexDirection: 'row', // Keep buttons in a row
          justifyContent: 'space-between', // Align Back to the left and Next to the right
          alignItems: 'center',
          padding: '0', // Remove extra padding
          gap: '0', // Remove gaps between buttons
          '@media (max-width: 768px)': {
            left: '1rem', // Adjust left margin for smaller screens
            width: 'calc(100% - 2rem)', // Adjust width for smaller screens
          },
        }}
      >
        <Button
          variant="contained"
          style={{
            backgroundColor: '#F6EB39',
            color: '#2E2E38',
            fontFamily: 'Helvetica Neue',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '22px',
            textAlign: 'center',
          }}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          variant="contained"
          style={{
            backgroundColor: '#F6EB39',
            color: '#2E2E38',
            fontFamily: 'Helvetica Neue',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '22px',
            textAlign: 'center',
          }}
          onClick={onNext}
        >
          Next
        </Button>
      </Box>
    </>
  );
};

export default BillingInformation;